import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export function JournalTopRankedChart({ data: externalData = null, onBarClick = null }) {
  const ref = useRef(null);
  const tooltipRef = useRef(null);
  const [activeTab, setActiveTab] = useState('WOS'); // 'WOS' | 'Scopus' | 'KCI'

  // API에서 데이터 가져오기 (externalData가 없을 때만)
  const [topRankedData, setTopRankedData] = useState(null);
  const [topRankedLoading, setTopRankedLoading] = useState(false);
  const [topRankedErr, setTopRankedErr] = useState(null);

  useEffect(() => {
    // data prop이 제공되면 API 호출하지 않음
    const hasExternalData = externalData !== null &&
      externalData !== undefined &&
      (Array.isArray(externalData) || (typeof externalData === 'object' && externalData !== null));

    if (hasExternalData) {
      return;
    }

    let alive = true;
    async function fetchTopRanked() {
      setTopRankedLoading(true);
      setTopRankedErr(null);
      try {
        const path = '/rm/v1/publication/top_ranked';

        const response = await get(path);
        if (alive) {
          if (response?.status && response?.result) {
            setTopRankedData(response.result);
            // 첫 번째 사용 가능한 탭으로 설정
            if (response.result.WOS && response.result.WOS.length > 0) {
              setActiveTab('WOS');
            } else if (response.result.Scopus && response.result.Scopus.length > 0) {
              setActiveTab('Scopus');
            } else if (response.result.KCI && response.result.KCI.length > 0) {
              setActiveTab('KCI');
            }
          } else {
            setTopRankedErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setTopRankedData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setTopRankedErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setTopRankedLoading(false);
        }
      }
    }
    fetchTopRanked();
    return () => { alive = false; };
  }, [externalData]);

  // Track container size reactively to avoid rendering with width=0 on initial paint
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Measure container before paint to avoid initial 0-width draw and collapse rapid resize notifications
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;

    // Initial measure
    let tId = 0; // debounce timer id
    const measureNow = () => {
      const rect = el.getBoundingClientRect?.();
      const w = (rect?.width ?? el.clientWidth) || 0;
      const h = (rect?.height ?? el.clientHeight) || 0;
      setContainerSize(prev => (prev.width !== w || prev.height !== h ? { width: w, height: h } : prev));
    };
    const measure = () => {
      if (tId) clearTimeout(tId);
      tId = setTimeout(measureNow, 60);
    };
    measure();

    // Observe size changes
    let ro;
    try {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    } catch (e) {
      // Fallback: window resize listener (older browsers)
      const onResize = () => measure();
      window.addEventListener('resize', onResize);
      // Also re-measure on next frame in case of late layout/fonts
      const raf = requestAnimationFrame(measure);
      return () => {
        window.removeEventListener('resize', onResize);
        cancelAnimationFrame(raf);
      };
    }

    // Re-measure after fonts load (affects layout in some cases)
    try {
      if (document && document.fonts && typeof document.fonts.ready?.then === 'function') {
        document.fonts.ready.then(() => measure());
      }
    } catch { }

    return () => {
      if (tId) clearTimeout(tId);
      try { ro && ro.disconnect(); } catch { }
    };
  }, []);

  // 탭 목록 생성
  const tabs = useMemo(() => {
    const data = externalData || topRankedData;
    if (!data || typeof data !== 'object') return [];

    const t = [];
    if (Array.isArray(data.WOS) && data.WOS.length > 0) t.push({ key: 'WOS', label: 'JCR' });
    if (Array.isArray(data.Scopus) && data.Scopus.length > 0) t.push({ key: 'Scopus', label: 'Scopus' });
    if (Array.isArray(data.KCI) && data.KCI.length > 0) t.push({ key: 'KCI', label: 'KCI' });
    return t;
  }, [externalData, topRankedData]);

  // 현재 활성 탭의 데이터 가져오기
  const data = useMemo(() => {
    const source = externalData || topRankedData;
    if (!source || typeof source !== 'object') return [];

    // top_categories 구조: {WOS: [...], Scopus: [...], KCI: [...]}
    if (source.WOS || source.Scopus || source.KCI) {
      const activeData = source[activeTab] || [];
      if (Array.isArray(activeData)) {
        return activeData.map(r => ({
          label: r.label || r.title || '',
          value: typeof r.value === 'number' ? r.value : (typeof r.count === 'number' ? r.count : 0)
        }));
      }
    }

    // 기존 구조 지원 (records 또는 배열)
    const transform = (raw) => {
      if (Array.isArray(raw)) {
        return raw.map(r => ({
          label: r.title || r.label || '',
          value: typeof r.count === 'number' ? r.count : (typeof r.value === 'number' ? r.value : 0)
        }));
      }
      if (raw && Array.isArray(raw.records)) {
        return raw.records.map(r => ({ label: r.title, value: r.count }));
      }
      return [];
    };

    // If an external array is provided, use it directly (non-tabbed use case)
    if (externalData && Array.isArray(externalData)) {
      return transform(externalData);
    }

    // Use API data if available
    if (topRankedData) {
      if (Array.isArray(topRankedData.records)) {
        return transform(topRankedData.records);
      }
      if (Array.isArray(topRankedData)) {
        return transform(topRankedData);
      }
    }

    return [];
  }, [externalData, topRankedData, activeTab]);

  // compute sizes from container
  // Prevent duplicate identical draws (e.g., React 18 StrictMode dev double-invoke or rapid RO bursts)
  const lastDrawRef = useRef({ key: '', width: 0 });

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    // Avoid rendering when container has no layout yet; wait for ResizeObserver to provide width
    const measuredWidth = containerSize.width;
    if (!measuredWidth || measuredWidth <= 0) return;

    // 데이터가 없으면 렌더링하지 않음
    if (!Array.isArray(data) || data.length === 0) return;

    // Build a draw key to detect identical consecutive renders
    const dataKey = Array.isArray(data) ? data.map(d => `${d.label}:${d.value}`).join('|') : 'none';
    const drawKey = `${Math.round(measuredWidth)}::${dataKey}`;

    // If nothing effectively changed (width diff < 2px and same data), skip
    const prev = lastDrawRef.current;
    const widthChanged = Math.abs((prev.width || 0) - measuredWidth) >= 2;
    if (!widthChanged && prev.key === drawKey) {
      return;
    }

    lastDrawRef.current = { key: drawKey, width: measuredWidth };

    // cleanup previous
    container.innerHTML = '';

    const width = measuredWidth; // use measured width; SVG will be width:100% with matching viewBox

    // Y축 라벨 길이에 따라 left margin 동적 계산 (2-3줄로 나뉘므로 줄당 길이 고려)
    const categories = data.map(d => d.label);
    // 라벨을 2-3줄로 나누므로 줄당 최대 25자 정도로 계산
    const maxLineLength = 25;
    // 라벨 영역을 적절히 조정 (최소 60px, 최대 70px)
    const leftMargin = Math.min(Math.max(60, maxLineLength * 2.5), 70);

    const margin = { top: 15, right: 20, bottom: 25, left: leftMargin };

    // 막대 개수 기준으로 높이 계산 (막대 두께 + 간격)
    const rowHeight = 40; // 막대 간격 더 줄임
    const baseHeight = margin.top + margin.bottom + rowHeight * Math.max(data.length, 5);
    const height = Math.max(280, baseHeight); // 최소 높이 280px로 더 줄임

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const maxVal = d3.max(data, d => d.value) || 0;

    const x = d3.scaleLinear().domain([0, Math.max(maxVal, 1)]).range([0, innerW]).nice();
    const y = d3.scaleBand().domain(categories).range([0, innerH]).padding(0.2);


    // gridlines
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(6).tickSize(-innerH).tickFormat(d => `${d}`))
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('line').attr('stroke', '#E4E8F1');
        g.selectAll('text').attr('fill', '#8D9AAF').style('font-size', 13);
      });

    // 라벨을 여러 줄로 나누는 함수
    const splitLabel = (text, maxLength = 20) => {
      if (!text || text.length <= maxLength) return [text];

      // 쉼표를 기준으로 먼저 분할
      const parts = text.split(',').map(p => p.trim());
      const lines = [];
      let currentLine = '';

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const testLine = currentLine ? `${currentLine}, ${part}` : part;

        if (testLine.length <= maxLength) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          // 현재 part가 너무 길면 공백으로도 분할
          if (part.length > maxLength) {
            const words = part.split(' ');
            let wordLine = '';
            for (const word of words) {
              const testWordLine = wordLine ? `${wordLine} ${word}` : word;
              if (testWordLine.length <= maxLength) {
                wordLine = testWordLine;
              } else {
                if (wordLine) lines.push(wordLine);
                wordLine = word;
              }
            }
            if (wordLine) currentLine = wordLine;
          } else {
            currentLine = part;
          }
        }
      }
      if (currentLine) lines.push(currentLine);

      return lines.length > 0 ? lines : [text];
    };

    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('text')
          .attr('fill', '#6C7687')
          .style('font-weight', 400)
          .style('font-size', 12)
          .attr('x', -10)
          .attr('text-anchor', 'end')
          .each(function (d) {
            const text = d3.select(this);
            const lines = splitLabel(d, 20); // 최대 길이를 20자로 조정
            const lineHeight = 1.15; // 줄 간격
            const yPos = text.attr('y');

            // 첫 번째 줄은 원래 위치에
            text.text(lines[0] || '');

            // 나머지 줄들은 tspan으로 추가
            lines.slice(1).forEach((line, i) => {
              text.append('tspan')
                .attr('x', -10)
                .attr('dy', `${lineHeight}em`)
                .attr('text-anchor', 'end')
                .text(line);
            });
          });
      });

    g
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', d => y(d.label))
      .attr('height', y.bandwidth())
      .attr('rx', 4)
      .attr('fill', '#6EA2FF')
      .attr('opacity', 0.9)
      .attr('width', 0)
      .transition()
      .duration(600)
      .attr('width', d => x(d.value));

    // hover interaction with tooltip
    const tooltip = d3.select(tooltipRef.current);

    g.selectAll('.hit-area')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'hit-area')
      .attr('x', 0)
      .attr('y', d => y(d.label))
      .attr('height', y.bandwidth())
      .attr('width', innerW)
      .attr('fill', 'transparent')
      .style('cursor', onBarClick ? 'pointer' : 'default')
      .on('mousemove', function (event, d) {
        const [mx, my] = d3.pointer(event, this);
        tooltip
          .style('display', 'block')
          .style('left', `${margin.left + mx - 60}px`)
          .style('top', `${margin.top + my - 44}px`)
          .html(`<div class="flex items-center gap-2"><span class="inline-block w-[8px] h-[8px] rounded-full bg-[#6EA2FF]"></span><span class="font-semibold text-[#2A2E36]">${d.label}</span><span class="ml-4 text-[#2A2E36]">${d.value}</span></div>`);
      })
      .on('mouseleave', () => tooltip.style('display', 'none'))
      .on('click', function (event, d) {
        if (onBarClick && d.label) {
          onBarClick(d.label);
        }
      });
  }, [data, containerSize.width]);

  // 데이터가 없을 때 처리
  const hasData = Array.isArray(data) && data.length > 0;
  const isJournalsPage = typeof window !== 'undefined' && window.location.pathname.includes('/journals');

  return (
    <>
      <div className="relative min-h-[280px]">
        {/* 탭 UI (journals 페이지에서만 표시) */}
        {isJournalsPage && tabs.length > 0 && (
          <ul className="mb-0 p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] inline-flex text-[15px] gap-[5px]">
            {tabs.map(t => (
              <li
                key={t.key}
                className={(activeTab === t.key ? 'active bg-white text-[#1F2937] ' : '') + 'w-[104px] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer transition-colors'}
                onClick={() => setActiveTab(t.key)}
                data-value={t.key}
              >
                {t.label}
              </li>
            ))}
          </ul>
        )}

        <div ref={ref} className="w-full" />
        {/* 로딩 상태 오버레이 */}
        <ChartSkeletonLoader show={externalData === null && topRankedLoading} />
        {/* 에러 상태 오버레이 */}
        {externalData === null && topRankedErr && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
            {topRankedErr}
          </div>
        )}
        {/* 데이터 없음 오버레이 */}
        {!hasData && !topRankedLoading && !topRankedErr && (
          <div className="absolute inset-0 flex items-center justify-center text-[#8D9AAF]">
            데이터가 없습니다.
          </div>
        )}
        <div
          ref={tooltipRef}
          className="absolute z-10 hidden px-3 py-2 bg-white border border-[#E4E8F1] rounded-xl shadow-sm text-[13px]"
          style={{ display: 'none' }}
        />
      </div>
    </>
  );
}

export default JournalTopRankedChart;
